import { Card, Typography, Space, Button, Row, Col, Alert } from "antd";
import { IInstitution } from "../../redux/reducer/institution";
import ConfigurationFormCard from "../molecules/ConfigurationFormCard";
import { CSSProperties, useState } from "react";
import {
    ApiOutlined,
    LinkOutlined,
    InfoCircleOutlined,
    MinusOutlined,
    PlusOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

interface IntegrationConfigurationProps {
    institution: IInstitution;
}

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '24px',
    },
    section: {
        marginBottom: '32px',
    },
    sectionTitle: {
        marginBottom: '16px',
        fontSize: '18px',
        fontWeight: 600,
    },
    cardWrapper: {
        height: '100%',
    },
    infoAlert: {
        marginBottom: '24px',
    },
};

const IntegrationConfiguration = ({
    institution,
}: IntegrationConfigurationProps) => {
    const [minimizedSections, setMinimizedSections] = useState<string[]>([]);

    const toggleMinimize = (sectionKey: string) => {
        setMinimizedSections(prev =>
            prev.includes(sectionKey)
                ? prev.filter(key => key !== sectionKey)
                : [...prev, sectionKey]
        );
    };

    return (
        <div style={styles.container}>
            <Alert
                message="Integration Settings"
                description="Configure webhooks and API access for your institution. Webhooks allow real-time notifications of events, while the API enables programmatic access to your data."
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={styles.infoAlert}
            />

            <div style={styles.section}>
                <Card
                    title={
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Title level={4} style={{ margin: 0 }}>
                                    <LinkOutlined /> Webhooks Configuration
                                </Title>
                            </Col>
                            <Col>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={minimizedSections.includes('webhooks') ? <PlusOutlined /> : <MinusOutlined />}
                                    onClick={() => toggleMinimize('webhooks')}
                                    title={minimizedSections.includes('webhooks') ? "Expand" : "Minimize"}
                                />
                            </Col>
                        </Row>
                    }
                    styles={{ body: minimizedSections.includes('webhooks') ? { display: 'none' } : {} }}
                >
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <div>
                            <Text type="secondary">
                                Configure webhook endpoints to receive real-time notifications when events occur in your institution.
                            </Text>
                        </div>
                        <ConfigurationFormCard
                            type="webhook"
                            params={{ hooks: institution.webhooks }}
                            institution={institution}
                        />
                    </Space>
                </Card>
            </div>

            <div style={styles.section}>
                <Card
                    title={
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Title level={4} style={{ margin: 0 }}>
                                    <ApiOutlined /> API Configuration
                                </Title>
                            </Col>
                            <Col>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={minimizedSections.includes('api') ? <PlusOutlined /> : <MinusOutlined />}
                                    onClick={() => toggleMinimize('api')}
                                    title={minimizedSections.includes('api') ? "Expand" : "Minimize"}
                                />
                            </Col>
                        </Row>
                    }
                    styles={{ body: minimizedSections.includes('api') ? { display: 'none' } : {} }}
                >
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <div>
                            <Text type="secondary">
                                Enable and configure API access for programmatic integration with your institution's data.
                            </Text>
                        </div>
                        <ConfigurationFormCard
                            type="api"
                            params={institution.api ?? { 
                                enabled: false, 
                                tenantId: institution.institutionId 
                            }}
                            institution={institution}
                        />
                    </Space>
                </Card>
            </div>
        </div>
    );
};

export default IntegrationConfiguration;
